import { Button, DismissibleTag } from '@carbon/react';
import { Add } from '@carbon/icons-react';
import { useTranslation, type I18nKey } from '@pos/i18n';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useAdmin } from '../adminContext';
import { ListTable, type ListColumn } from '../components/ListTable';
import { PageHeader } from '../components/PageHeader';
import { DateRangeFilter } from '../fields/DateField';
import { useReferenceOptions } from '../fields/references';
import { FilterLayout } from '../filters/FilterPanel';
import { ChoiceDropdown } from '../filters/ResourceFilters';
import { useFilters } from '../filters/useFilters';
import { asProblem, problemMessage } from '../problems';
import { hasPermission } from '../session';
import {
  ORDER_PERMISSIONS,
  FULFILMENT_STATUSES,
  KITCHEN_STATUSES,
  ORDER_STATUSES,
  PAYMENT_STATUSES,
  ordersKey,
  useFormat,
  useOrderSetup,
  useOrdersApi,
  type OrdersMode,
} from './model';
import type { OrderSummary } from './ordersApi';
import { OrderStatusTags } from './OrderStatusTag';
import './orders.scss';

const PAGE_SIZES = [25, 50, 100];

interface Option {
  id: string;
  label: string;
}

/** Local date (yyyy-mm-dd) to the UTC instant it starts at, for the opened-at filter. */
const startOf = (date: string) => (date ? new Date(`${date}T00:00:00`).toISOString() : undefined);

/** Orders of this store (store portal) or of every store (HO), newest first, with search and filters (FR-ORD-31). */
export function OrdersPage({ mode }: { mode: OrdersMode }) {
  const { t } = useTranslation();
  const { session } = useAdmin();
  const api = useOrdersApi();
  const navigate = useNavigate();
  const format = useFormat();
  const { setup, saleTypeName } = useOrderSetup();
  const stores = useReferenceOptions(mode === 'ho' ? 'org.store' : undefined);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0] ?? 25);
  const filters = useFilters(
    { status: '', payment: '', kitchen: '', fulfilment: '', saleType: '', storeId: '', member: '', from: '', to: '' },
    () => setPage(1),
  );
  const { status, payment, kitchen, fulfilment, saleType, storeId, member, from, to } = filters.applied;
  // One member's orders, opened from their account at HO (`?memberId=`); the id stays out of the filters panel.
  const [query, setQuery] = useSearchParams();
  const memberId = query.get('memberId') ?? undefined;
  const canCreate = mode === 'store' && hasPermission(session, ORDER_PERMISSIONS.manage);

  const params = {
    search: search || undefined,
    status: status || undefined,
    payment: payment || undefined,
    kitchen: kitchen || undefined,
    fulfilment: fulfilment || undefined,
    saleType: saleType || undefined,
    storeId: storeId || undefined,
    member: member || undefined,
    memberId,
    from: startOf(from),
    to: to ? new Date(new Date(`${to}T00:00:00`).getTime() + 86_400_000).toISOString() : undefined,
    page,
    pageSize,
  };
  const list = useQuery({
    queryKey: [...ordersKey, 'list', params],
    queryFn: () => api.list(params),
    placeholderData: keepPreviousData,
  });

  const all: Option = { id: '', label: t('orders.all') };
  const statusOptions: Option[] = [
    all,
    ...ORDER_STATUSES.map((s) => ({ id: s, label: t(`orders.status.${s}` as I18nKey) })),
  ];
  const paymentOptions: Option[] = PAYMENT_STATUSES.map((s) => ({ id: s, label: t(`orders.paymentStatus.${s}` as I18nKey) }));
  const kitchenOptions: Option[] = KITCHEN_STATUSES.map((s) => ({ id: s, label: t(`orders.kitchenStatus.${s}` as I18nKey) }));
  const fulfilmentOptions: Option[] = FULFILMENT_STATUSES.map((s) => ({
    id: s,
    label: t(`orders.fulfilmentStatus.${s}` as I18nKey),
  }));
  const saleTypeOptions: Option[] = [
    all,
    ...(setup?.saleTypes ?? []).map((s) => ({ id: s.code, label: saleTypeName(s.id, s.code) })),
  ];
  const storeOptions: Option[] = [all, ...(stores.data ?? []).map((s) => ({ id: s.value, label: s.label }))];
  const storeName = (id: string) => stores.data?.find((s) => s.value === id)?.label ?? '';
  const memberOptions: Option[] = [
    { id: 'yes', label: t('orders.filters.withMember') },
    { id: 'no', label: t('orders.filters.withoutMember') },
  ];
  const memberOf = (o: OrderSummary) => [o.memberAccountNo, o.memberName].filter(Boolean).join(' · ');

  const columns: ListColumn<OrderSummary>[] = [
    { key: 'orderNumber', header: t('orders.columns.number'), sortable: true },
    ...(mode === 'ho'
      ? [
          {
            key: 'store',
            header: t('orders.columns.store'),
            render: (o: OrderSummary) => storeName(o.storeId),
            sortValue: (o: OrderSummary) => storeName(o.storeId),
          },
        ]
      : []),
    {
      key: 'openedAt',
      header: t('orders.columns.openedAt'),
      sortable: true,
      render: (o) => format.dateTime(o.openedAt),
    },
    {
      key: 'saleType',
      header: t('orders.columns.saleType'),
      render: (o) => saleTypeName(o.saleTypeId, o.saleTypeCode),
    },
    {
      key: 'guests',
      header: t('orders.columns.guests'),
      className: 'pos-admin-page__number',
      render: (o) => (o.guestCount == null ? '' : format.number(o.guestCount)),
    },
    {
      key: 'customer',
      header: t('orders.columns.customer'),
      render: (o) => [o.customerName, o.customerPhone].filter(Boolean).join(' · '),
    },
    { key: 'member', header: t('orders.columns.member'), render: memberOf, sortValue: memberOf },
    { key: 'status', header: t('orders.columns.status'), render: (o) => <OrderStatusTags order={o} /> },
    {
      key: 'total',
      header: t('orders.columns.total'),
      sortable: true,
      className: 'pos-admin-page__number',
      render: (o) => format.money(o.total, o.currencyCode),
    },
    {
      key: 'balance',
      header: t('orders.columns.balance'),
      className: 'pos-admin-page__number',
      render: (o) => (o.isEditable ? format.money(o.balance, o.currencyCode) : ''),
    },
  ];
  const items = list.data?.items ?? [];
  const reset =
    <T,>(set: (value: T) => void) =>
    (value: T) => {
      set(value);
      setPage(1);
    };

  return (
    <section className="pos-admin-page">
      <PageHeader
        title={t('orders.title')}
        description={mode === 'ho' ? t('orders.readOnly') : undefined}
        actions={
          canCreate && (
            <Button renderIcon={Add} onClick={() => void navigate('/orders/new')}>
              {t('orders.createOrder')}
            </Button>
          )
        }
      />
      {memberId && (
        <DismissibleTag
          type="blue"
          text={t('orders.memberOnly', { member: items.find((o) => o.memberId === memberId)?.memberAccountNo ?? '' })}
          dismissTooltipLabel={t('orders.allMembers')}
          onClose={() => {
            query.delete('memberId');
            setQuery(query);
            setPage(1);
          }}
        />
      )}
      <FilterLayout
        filters={filters}
        fields={
          <>
            <ChoiceDropdown
              id="orders-status"
              label={t('orders.filters.status')}
              choices={statusOptions.filter((o) => o.id)}
              value={filters.draft.status}
              onChange={(v) => filters.set('status', v)}
            />
            <ChoiceDropdown
              id="orders-payment"
              label={t('orders.filters.payment')}
              choices={paymentOptions}
              value={filters.draft.payment}
              onChange={(v) => filters.set('payment', v)}
            />
            <ChoiceDropdown
              id="orders-kitchen"
              label={t('orders.filters.kitchen')}
              choices={kitchenOptions}
              value={filters.draft.kitchen}
              onChange={(v) => filters.set('kitchen', v)}
            />
            <ChoiceDropdown
              id="orders-fulfilment"
              label={t('orders.filters.fulfilment')}
              choices={fulfilmentOptions}
              value={filters.draft.fulfilment}
              onChange={(v) => filters.set('fulfilment', v)}
            />
            <ChoiceDropdown
              id="orders-sale-type"
              label={t('orders.filters.saleType')}
              choices={saleTypeOptions.filter((o) => o.id)}
              value={filters.draft.saleType}
              onChange={(v) => filters.set('saleType', v)}
            />
            <ChoiceDropdown
              id="orders-member"
              label={t('orders.filters.member')}
              choices={memberOptions}
              value={filters.draft.member}
              onChange={(v) => filters.set('member', v)}
            />
            {mode === 'ho' && (
              <ChoiceDropdown
                id="orders-store"
                label={t('orders.filters.store')}
                choices={storeOptions.filter((o) => o.id)}
                value={filters.draft.storeId}
                onChange={(v) => filters.set('storeId', v)}
              />
            )}
            <DateRangeFilter
              id="orders"
              legendText={t('orders.filters.openedAt')}
              fromLabel={t('orders.filters.from')}
              toLabel={t('orders.filters.to')}
              from={filters.draft.from}
              to={filters.draft.to}
              onChange={(range) => {
                filters.set('from', range.from);
                filters.set('to', range.to);
              }}
            />
          </>
        }
      >
        <ListTable
          ariaLabel={t('orders.title')}
          columns={columns}
          rows={items}
          loading={list.isLoading}
          error={list.error ? problemMessage(t, asProblem(list.error)) : undefined}
          onRetry={() => void list.refetch()}
          search={{ label: t('orders.search'), onChange: reset(setSearch) }}
          filters={filters}
          onRowClick={(order) => void navigate(`/orders/${order.id}`)}
          empty={
            mode === 'ho' && !search && filters.count === 0
              ? { heading: t('orders.hoEmpty') }
              : undefined
          }
          pagination={{
            page,
            pageSize,
            pageSizes: PAGE_SIZES,
            total: list.data?.total ?? 0,
            capped: list.data?.totalCapped,
            onChange: ({ page: nextPage, pageSize: nextSize }) => {
              setPage(nextPage);
              setPageSize(nextSize);
            },
          }}
        />
      </FilterLayout>
    </section>
  );
}
